"use client";

import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useAtomValue } from "jotai";
import ResponsiveModal from "~/components/modal/ResponsiveModal";
import { QUERY_PARAM_POFE, QUERY_PARAM_POFE_DETAILS_MODAL, QUERY_PARAM_PURCHASE_ORDER } from "~/constants/queryParams";
import { useI18n } from "~/locales/client";
import { updateSearchParams } from "~/utils/clientUtils";
import POFEDetails from "./POFEDetails";
import { purchaseOrdersAtom } from "~/atoms";
import Flex from "~/components/flex/Flex";
import Text from "~/components/texts/Text";
import Badge from "~/components/badges/Badge";
import {
  getBgColorForPofeStatus,
  getBgColorForPofePaymentStatus,
  getColorForPofePaymentStatus,
  getColorForPofeStatus,
  getPofeStatusTranslationKey,
} from "~/utils/purchaseOrdersUtil";
import { PofeStatusTypeEnum } from "~/types-and-enums/purchaseOrderTypes";

const RIGHT_SIDE_MODAL_ANIMATION_DURATION = 750;

/**
 * POFE Details Modal component
 * @returns The POFE Details Modal component
 */
const POFEDetailsModal = () => {
  const translate = useI18n();
  const searchParams = useSearchParams();
  const purchaseOrdersState = useAtomValue(purchaseOrdersAtom);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [shouldRender, setShouldRender] = useState(true);
  const closeTimerRef = useRef<number | undefined>(undefined);
  const requestedPofeId = searchParams.get(QUERY_PARAM_POFE);
  const requestedPoId = searchParams.get(QUERY_PARAM_PURCHASE_ORDER);
  const selectedPOFromQuery = requestedPoId
    ? purchaseOrdersState.find((order) => order.id === requestedPoId)
    : undefined;
  const selectedPOFromPofe = requestedPofeId
    ? purchaseOrdersState.find((order) => order.po_fulfillment_events.some((pofe) => pofe.id === requestedPofeId))
    : undefined;
  const resolvedSelectedPO = selectedPOFromQuery ?? selectedPOFromPofe ?? purchaseOrdersState?.[0];
  const selectedPofeNumber =
    requestedPofeId && resolvedSelectedPO?.po_fulfillment_events?.some((pofe) => pofe.id === requestedPofeId)
      ? requestedPofeId
      : resolvedSelectedPO?.po_fulfillment_events?.[0]?.id;
  const selectedPofeDetails = resolvedSelectedPO?.po_fulfillment_events.find(
    (pofeEvent) => pofeEvent.id === selectedPofeNumber
  );

  const removeModalParam = () => {
    const params = new URLSearchParams(searchParams);
    params.delete(QUERY_PARAM_POFE_DETAILS_MODAL);
    updateSearchParams(params);
  };

  const handleCloseModal = () => {
    if (!isModalOpen) return;

    setIsModalOpen(false);
    closeTimerRef.current = window.setTimeout(() => {
      setShouldRender(false);
      removeModalParam();
    }, RIGHT_SIDE_MODAL_ANIMATION_DURATION);
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  if (!shouldRender) return null;

  const status = translate(getPofeStatusTranslationKey(selectedPofeDetails?.status as PofeStatusTypeEnum));
  const paymentStatus = translate(
    `general.payment-status.${selectedPofeDetails?.balance_payment_status?.toLowerCase()}`
  );
  const isCancelled = selectedPofeDetails?.status === PofeStatusTypeEnum.CANCELLED;
  const statusColor = getColorForPofeStatus(selectedPofeDetails?.status as PofeStatusTypeEnum);
  const statusBgColor = getBgColorForPofeStatus(selectedPofeDetails?.status as PofeStatusTypeEnum);
  const paymentStatusColor = getColorForPofePaymentStatus(selectedPofeDetails?.balance_payment_status ?? "");
  const paymentStatusBgColor = getBgColorForPofePaymentStatus(selectedPofeDetails?.balance_payment_status ?? "");

  const title = (
    <Flex className="gap-3 my-2 shrink-0">
      <Text variant="h3">{translate("general.item") + " #" + selectedPofeDetails?.name.split("-")[1].trim()}</Text>
      <Badge className={statusColor + " " + statusBgColor}>{status}</Badge>
      {!isCancelled && <Badge className={paymentStatusColor + " " + paymentStatusBgColor}>{paymentStatus}</Badge>}
    </Flex>
  );

  return (
    <ResponsiveModal
      title={title}
      isOpen={isModalOpen}
      onRequestClose={handleCloseModal}
      cardClassName="pt-2 pb-0"
      animationDuration={RIGHT_SIDE_MODAL_ANIMATION_DURATION}
    >
      {selectedPofeNumber && (
        <POFEDetails
          handleCloseModal={handleCloseModal}
          selectedPofeDetails={selectedPofeDetails}
          selectedPofeNumber={selectedPofeNumber}
          selectedPONumber={resolvedSelectedPO?.id}
        />
      )}
    </ResponsiveModal>
  );
};

export default POFEDetailsModal;
